import { cn } from "@/utils/cn";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Checkbox } from "@babackapp/ui/components/checkbox";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import { ChevronsUpDown, Laptop, ShoppingBag, Store, Tag } from "lucide-react";
import { useState, useMemo, ElementType } from "react";

type SourceChannel = "online" | "pos" | "marketplace" | "manual";

type ChannelOption = {
  value: SourceChannel;
  labelKey: string;
  Icon: ElementType;
};

const CHANNEL_OPTIONS: ChannelOption[] = [
  { value: "online", labelKey: "analytics.filter.sourceChannel.online", Icon: Laptop },
  { value: "pos", labelKey: "analytics.filter.sourceChannel.pos", Icon: Store },
  { value: "marketplace", labelKey: "analytics.filter.sourceChannel.marketplace", Icon: ShoppingBag },
  { value: "manual", labelKey: "analytics.filter.sourceChannel.manual", Icon: Tag },
];

type SourceChannelSelectProps = {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
};

export function SourceChannelSelect({ value, onChange }: SourceChannelSelectProps) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);

  const selectedChannels = useMemo(() => {
    if (!value) return new Set<SourceChannel>();
    return new Set(value.split(",").filter(Boolean) as SourceChannel[]);
  }, [value]);

  const toggleChannel = (channel: SourceChannel) => {
    const newSet = new Set(selectedChannels);
    if (newSet.has(channel)) {
      newSet.delete(channel);
    } else {
      newSet.add(channel);
    }

    if (newSet.size === 0) {
      onChange(undefined);
    } else {
      onChange(Array.from(newSet).join(","));
    }
  };

  const getButtonLabel = () => {
    if (selectedChannels.size === 0) {
      return t("analytics.filter.sourceChannel.all");
    }
    if (selectedChannels.size === 1) {
      const channel = Array.from(selectedChannels)[0];
      const option = CHANNEL_OPTIONS.find((o) => o.value === channel);
      return option ? t(option.labelKey) : channel;
    }
    return t("analytics.filter.sourceChannel.multiple", { count: selectedChannels.size });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          <div className="flex gap-2 items-center">
            <ShoppingBag className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{getButtonLabel()}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandList>
            <CommandGroup>
              {CHANNEL_OPTIONS.map((option) => {
                const Icon = option.Icon;
                const isSelected = selectedChannels.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => toggleChannel(option.value)}
                  >
                    <Checkbox
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-50",
                      )}
                      checked={isSelected}
                    />
                    <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    {t(option.labelKey)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
